import { namespace } from 'wsm-tagging-utils';

const runLinkClickFilterDecorator = (win) => {
	namespace(win, 'DDC.tracking.adobe_launch.decorators');

	const {
		DDC,
		DDC: { tracking }
	} = win;

	/**
	 * The Link Click Filter Decorator acts to filter out (drop) link click events that occur when another, more specific
	 * tracking event would also occur. This prevents the appearance of "duplicate" click events from being sent out via
	 * the EventHandler.
	 *
	 *
	 * @param {Object} window The window object
	 * @param {tracking.framework.runtime.Page} page A Page instance for page related queries
	 * @param {tracking.framework.runtime.EventHandler} delegate The EventHandler implementation to which all interface operations are delegated
	 * @constructor
	 */
	tracking.adobe_launch.decorators.LinkClickFilterDecorator = class extends (
		tracking.framework.runtime.EventHandlerDecorator
	) {
		/**
		 * Use this to set up the list of selectors for OTHER events on the system that could occur at the same time as a generic click.
		 * Most will be in `DDC.dataLayer.events.selectors` but we may need to add others for cases where the ddc-data-layer is not the
		 * source for the event.
		 */
		initFilterSelectors() {
			// FIXME: this is a problem with the ddc-data-layer widget writing directly to the DDC.dataLayer object. While the 'events.selectors'
			// are present on the DDC.dataLayer object, the DataLayerHelper's model is not being updated and so returns 'undefined'
			// const eventSelectors = this.page.dataLayerProperty('events.selectors');
			const eventSelectors = DDC.dataLayer.events.selectors;

			if (eventSelectors) {
				this.filterSelectors = Object.values(eventSelectors);

				// Remove the generic `linkClick` selector, or we'll end up filtering ALL generic clicks out. Do not want.
				this.filterSelectors.splice(
					this.filterSelectors.findIndex((e) => /^a\[href\]/.test(e)),
					1
				);

				this.filterSelectors.push('.hproduct');

				// Remove the SRP specific elements from generic 'linkClick' event
				this.filterSelectors.push('.vehicle-card');
				this.filterSelectors.push('.DefaultHandle_handle');
				this.filterSelectors.push('.free-text-search-form');
				this.filterSelectors.push('.free-text-search-form');
				this.filterSelectors.push('.ws-inv-facets .panel-title a');

				// eslint-disable-next-line max-len
				// Note this data-widget-name doesn't match the trackEvent eventData.widgetName, because the actual widget fills in 'recommended_vehicles' for widgetName
				this.filterSelectors.push(
					'[data-widget-name="recommendations-vehicles"]'
				);

				this.filterSelectors.push(
					'[data-widget-name="ws-vehicle-media"]'
				);

				// Remove the previous and next arrows from ws-dealer-reviews
				/* eslint-disable */
				this.filterSelectors.push(
					'[data-widget-name="ws-dealer-reviews"] .slider-control-centerleft, [data-widget-name="ws-dealer-reviews"] .slider-control-centerright, [data-widget-name="ws-dealer-reviews"] .slider-control-bottomcenter'
				);
				/* eslint-enable */

				// Janky handling for a chat integration that's firing events we don't want
				/* eslint-disable */
				this.filterSelectors.push(
					'#caoEngagementFullBg, #caoAgentInfoPopBackground, #caoSecondaryEngagementContainer, #caoMobileChatOnlyArea, #lpChat, .lp_survey_container_modal'
				);
				/* eslint-enable */

				// Ignore anything that has any attr of "ui-tabs-initial-load", janky solution for jQuery's ui-tabs...
				this.filterSelectors.push('[ui-tabs-initial-load="true"]');

				this.filterSelectors.push(
					'.slideshow-background .pager a, .slideshow-default .pager a'
				);
			}
		}

		handleLinkClick(eventData) {
			if (!this.filterSelectors) {
				this.initFilterSelectors();
			}

			const { element } = eventData;

			// Use special logic that is not encompassed by the selector to determine if we really should filter a nav link click.
			// See: navigationClick() in v9/widgets/tracking/ddc-data-layer/v1/js/widget.js
			const isNavLink = (elem) => {
				let isParentLink;
				let isChildLink;
				let isHeadingLink;

				if (DDC.client.isMobile) {
					isParentLink =
						elem.classList.contains('accordion-faux') &&
						elem.classList.contains('.heading-tier2') === false;
					isChildLink = elem.classList.contains('tier3');
					isHeadingLink = elem.classList.contains('heading-tier2');
				} else {
					isParentLink =
						elem.closest('.navbar-nav') &&
						elem.parentElement.classList.contains(
							'nav-no-children'
						);
					isChildLink = elem.classList.contains('child');
					isHeadingLink = elem.parentElement.classList.contains('h4');
				}

				return isParentLink || isChildLink || isHeadingLink;
			};

			const closestWithLimit = (elemIn, selector, stopSelector) => {
				let elem = elemIn;
				let matchedElem = null;

				while (elem) {
					if (elem.matches(selector)) {
						// Navigation link special snowflake handling
						if (
							selector.includes('[data-navigation-id]') &&
							!isNavLink(elem)
						) {
							break;
						}

						matchedElem = elem;
						break;
					} else if (stopSelector && elem.matches(stopSelector)) {
						break;
					}

					elem = elem.parentElement;
				}

				return matchedElem;
			};

			const hasClosestMatch = (selector) =>
				closestWithLimit(element, selector, [
					'[data-widget-id]',
					'[data-web-api-id]'
				]) !== null && !element.hasAttribute('data-link-type');

			const foundFilterMatch = this.filterSelectors.some(hasClosestMatch);
			if (foundFilterMatch) {
				// Drop event
				this.log.info("dropping 'ddc.link.generic' click event!!");
			} else {
				this.delegate.handleLinkClick(eventData);
			}
		}
	};

	tracking.adobe_launch.decorators.LinkClickFilterDecorator.prototype.logCategory =
		'LinkClickFilterDecorator';
};

export default runLinkClickFilterDecorator;
